import { CircularProgress, DialogContent, DialogTitle } from '@mui/material';
import { Dialog } from '@mui/material';
import { WBBox, WBFlex, WBIconButton, WBTypography } from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import React, { Suspense, useMemo } from 'react';
import PageSelector from '../../../components/PageSelector/PageSelector';
import { useSelectedEntity } from '../../../hooks/useSelectedEntity/useSelectedEntity';
import { usePaymentEnabled } from '../../../hooks/usePaymentEnabled/usePaymentEnabled';
import Settings from '../../pages/Settings/Settings';
import { useSearchParams } from 'react-router-dom';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags/useFeatureFlags';

// Import settings page components - using dynamic imports to avoid errors if components don't exist
const Account = React.lazy(() => import('../../../pages/Account/Account'));
const Business = React.lazy(() => import('../../../pages/Business/Business'));
const PaymentMethods = React.lazy(
  () => import('../../../pages/PaymentMethods/PaymentMethods')
);
const ReceivingAccounts = React.lazy(
  () => import('../../../pages/ReceivingAccounts/ReceivingAccounts')
);
const Transactions = React.lazy(
  () => import('../../../pages/Transactions/Transactions')
);
const Users = React.lazy(() => import('../../../pages/Users/Users'));
const Support = React.lazy(() => import('../../../pages/Support/Support'));

export interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  tab: 'users';
}

export const SettingsModal = ({ open, onClose, tab }: SettingsModalProps) => {
  const { t } = useTranslation();
  const paymentEnabled = usePaymentEnabled();
  const { entity } = useSelectedEntity();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get('section');
  const { featureFlags } = useFeatureFlags();

  const createNavLink = (sectionName: string) => {
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set('section', sectionName);
    return `?${currentParams.toString()}`;
  };

  React.useEffect(() => {
    if (tab) {
      const currentParams = new URLSearchParams(searchParams);
      currentParams.set('section', tab);
      setSearchParams(currentParams);
    }
  }, [tab, searchParams, setSearchParams]);

  const navPaths = useMemo(
    () => [
      {
        to: createNavLink('account'),
        title: t('accountTitle', { ns: 'common' }),
        subTitle: t('accountSubTitle', { ns: 'common' }),
        icon: 'ChevronForward',
        target: '_self',
      },
      {
        to: createNavLink('business'),
        title: t('businessTitle', { ns: 'common' }),
        subTitle: t('businessSubTitle', {
          ns: 'common',
          business: entity?.name,
        }),
        icon: 'ChevronForward',
        target: '_self',
      },
      ...(paymentEnabled
        ? [
            {
              to: createNavLink('paymentMethods'),
              title: t('paymentTitle', { ns: 'common' }),
              subTitle: t('paymentSubTitle', { ns: 'common' }),
              icon: 'ChevronForward',
              target: '_self',
            },
            {
              to: createNavLink('receivingAccounts'),
              title: t('receivingAccountTitle', { ns: 'common' }),
              subTitle: t('receivingAccountSubTitle', { ns: 'common' }),
              icon: 'ChevronForward',
              target: '_self',
            },
            ...(featureFlags?.transactions
              ? [
                  {
                    to: createNavLink('transactions'),
                    title: t('transactionsTitle', { ns: 'common' }),
                    subTitle: t('transactionsSubTitle', { ns: 'common' }),
                    icon: 'ChevronForward',
                    target: '_self',
                  },
                ]
              : []),
          ]
        : []),
      {
        to: createNavLink('users'),
        title: t('usersTitle', { ns: 'common' }),
        subTitle: t('usersSubTitle', { ns: 'common' }),
        icon: 'ChevronForward',
        target: '_self',
      },
      {
        to: createNavLink('support'),
        title: t('supportTitle', { ns: 'common' }),
        subTitle: t('supportSubTitle', { ns: 'common' }),
        icon: 'Open',
        target: '_self',
      },
    ],
    [t, entity?.name, paymentEnabled, featureFlags?.transactions]
  );

  const renderContent = (section: string) => {
    return (
      <Suspense
        fallback={
          <WBBox display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </WBBox>
        }
      >
        <PageSelector current={section}>
          <PageSelector.Page value="account">
            <Account />
          </PageSelector.Page>
          <PageSelector.Page value="business">
            <Business />
          </PageSelector.Page>
          <PageSelector.Page value="paymentMethods">
            <PaymentMethods />
          </PageSelector.Page>
          <PageSelector.Page value="receivingAccounts">
            <ReceivingAccounts />
          </PageSelector.Page>
          <PageSelector.Page value="transactions">
            <Transactions />
          </PageSelector.Page>
          <PageSelector.Page value="users">
            <Users />
          </PageSelector.Page>
          <PageSelector.Page value="support">
            <Support />
          </PageSelector.Page>
        </PageSelector>
      </Suspense>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle variant="h1">
        <WBFlex justifyContent="space-between" alignItems="center">
          <WBTypography variant="h1">
            {t('settingsTitle', { ns: 'common' })}
          </WBTypography>
          <WBIconButton icon="Close" onClick={onClose} />
        </WBFlex>
      </DialogTitle>
      <DialogContent>
        {entity && (
          <Settings
            isClientContext={false}
            clientId={entity?.id}
            onBack={() => {
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('section');
              setSearchParams(newParams);
            }}
            customPaths={navPaths}
            renderContent={renderContent}
            activeSection={section}
            selectedEntity={entity}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
